import { Box, Tab, Tabs, Typography } from "@mui/material";
import React, { useState } from "react";

import { useCelleraTheme } from "../../theme/useCelleraTheme";

export interface TabConfig {
  label: string;
  content: React.ReactNode;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cellera-tabpanel-${index}`}
      aria-labelledby={`cellera-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface CustomTabsProps {
  tabs: TabConfig[];
  defaultTab?: number;
  onTabChange?: (tabIndex: number) => void;
  distributeEvenly?: boolean;
}

function CustomTabs({
  tabs,
  defaultTab = 0,
  onTabChange,
  distributeEvenly = false,
}: CustomTabsProps) {
  const { colors } = useCelleraTheme();
  const [activeTab, setActiveTab] = useState<number>(defaultTab);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  if (!tabs || tabs.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: colors.text.secondary,
          }}
        >
          No tabs available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Sticky Tabs Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: colors.background.paper,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="cellera tabs"
          variant={distributeEvenly ? "fullWidth" : "scrollable"}
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 500,
              color: colors.text.secondary,
              minWidth: "120px",
              whiteSpace: "nowrap",
            },
            "& .Mui-selected": {
              color: colors.primary.main,
            },
            "& .MuiTabs-scrollButtons": {
              color: colors.text.secondary,
              "&.Mui-disabled": {
                opacity: 0.3,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: colors.primary.main,
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Scrollable Tab Content */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {tabs.map((tab, index) => (
          <TabPanel key={index} value={activeTab} index={index}>
            {tab.content}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
}

export default CustomTabs;
